// The platform and directory ids as Effect Schema. Each decodes to the same
// branded type as its `@finnest/core/schemas` Zod schema, so an id flows
// unchanged between Effect owners and the banking, power and infra code still
// on Zod. The brand moves to `Schema.brand` when the last Zod importer leaves.
import { Schema } from 'effect';
import type {
  AuthorisationServerId as AuthorisationServerIdType,
  OrganisationId as OrganisationIdType,
} from '../../schemas/ids/authorisation-server.ts';
import type {
  ApiKeyId as ApiKeyIdType,
  TenantId as TenantIdType,
} from '../../schemas/ids/platform.ts';
import type { ReceiverConnectionId as ReceiverConnectionIdType } from '../../schemas/ids/receiver-connection.ts';
import type {
  CorrelationId as CorrelationIdType,
  RequestId as RequestIdType,
} from '../../schemas/ids/technical.ts';

const TENANT_ID = /^tnt_[0-9a-z]{24}$/u;
const API_KEY_ID = /^key_[0-9a-z]{24}$/u;

// Zod's `z.uuid()` pattern: RFC 9562 versions 1-8 with the RFC variant, plus
// the nil and the lowercase max UUID. Effect's `isUUID()` also takes an
// uppercase max, which the frozen Zod ids refuse.
const UUID =
  /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/u;

/** Stable opaque id for a Finnest tenant (bank or reseller): `tnt_<24 base36>`. */
export const TenantId = Schema.String.pipe(
  Schema.refine((value): value is TenantIdType => TENANT_ID.test(value), {
    identifier: 'TenantId',
    expected: "a tenant id 'tnt_<24 base36>'",
    toJsonSchema: () => ({ pattern: TENANT_ID.source }),
  }),
);
export type TenantId = typeof TenantId.Type;

/** A participant authorisation server in the OFB/OPIN participant directory. */
export const AuthorisationServerId = Schema.String.pipe(
  Schema.refine((value): value is AuthorisationServerIdType => UUID.test(value), {
    identifier: 'AuthorisationServerId',
    expected: 'an authorisation server UUID',
    toJsonSchema: () => ({ format: 'uuid' }),
  }),
);
export type AuthorisationServerId = typeof AuthorisationServerId.Type;

/** A directory organisation, shared by OFB and OPIN participant snapshots. */
export const OrganisationId = Schema.String.pipe(
  Schema.refine((value): value is OrganisationIdType => UUID.test(value), {
    identifier: 'OrganisationId',
    expected: 'an organisation UUID',
    toJsonSchema: () => ({ format: 'uuid' }),
  }),
);
export type OrganisationId = typeof OrganisationId.Type;

/** A receiver-side connection to a participant authorisation server. */
export const ReceiverConnectionId = Schema.String.pipe(
  Schema.refine((value): value is ReceiverConnectionIdType => UUID.test(value), {
    identifier: 'ReceiverConnectionId',
    expected: 'a receiver connection UUID',
    toJsonSchema: () => ({ format: 'uuid' }),
  }),
);
export type ReceiverConnectionId = typeof ReceiverConnectionId.Type;

/** Stable opaque id for an API key: `key_<24 base36>`; the secret is never exposed. */
export const ApiKeyId = Schema.String.pipe(
  Schema.refine((value): value is ApiKeyIdType => API_KEY_ID.test(value), {
    identifier: 'ApiKeyId',
    expected: "an api key id 'key_<24 base36>'",
  }),
);
export type ApiKeyId = typeof ApiKeyId.Type;

/** The id of one inbound request, for log and audit correlation. */
export const RequestId = Schema.String.pipe(
  Schema.refine((value): value is RequestIdType => UUID.test(value), {
    identifier: 'RequestId',
    expected: 'a request UUID',
  }),
);
export type RequestId = typeof RequestId.Type;

/** The id correlating one unit of work, such as a job run, across services. */
export const CorrelationId = Schema.String.pipe(
  Schema.refine((value): value is CorrelationIdType => UUID.test(value), {
    identifier: 'CorrelationId',
    expected: 'a correlation UUID',
  }),
);
export type CorrelationId = typeof CorrelationId.Type;
// The published OFB/OPIN `x-idempotency-key` pattern; see the Zod `IdempotencyKey`.
const IDEMPOTENCY_KEY = /^(?!\s)(.*)(\S)$/u;
const IDEMPOTENCY_KEY_MAX_LENGTH = 40;

/** The OFB/OPIN `x-idempotency-key`: a free string of 1-40 characters, never a UUID by spec. */
export const IdempotencyKey = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(IDEMPOTENCY_KEY_MAX_LENGTH),
  Schema.isPattern(IDEMPOTENCY_KEY),
).annotate({ identifier: 'IdempotencyKey' });
export type IdempotencyKey = typeof IdempotencyKey.Type;
